<template>
  <div class="row">
    <div class="page-title">
      <div class="row">
        <div class="col-12">
          <h2>HST Reserve</h2>
        </div>
      </div>
    </div>

    <div class="col-xl-12">
      <div class="row" v-if="summaryLoaded">
        <div class="col-md-4 col-6 mb-3" v-for="card in summaryCards" :key="card.label">
          <div class="card mb-0">
            <div class="card-body">
              <h6 class="text-muted mb-1">{{ card.label }}</h6>
              <h4 class="mb-0">{{ card.value }}</h4>
            </div>
          </div>
        </div>
      </div>
      <div v-else class="text-center py-4">
        <span class="spinner-border spinner-border-sm me-2"></span> Loading summary...
      </div>
      <div v-if="summaryLoaded && summary && summary.settledRidesMissingAccrualCount > 0" class="alert alert-warning">
        {{ summary.settledRidesMissingAccrualCount }} settled ride(s) in the last 12 months have no HST accrual yet.
      </div>
    </div>

    <div class="col-xl-12">
      <div class="card">
        <div class="card-header">
          <h5>Remittance-period report</h5>
        </div>
        <div class="card-body">
          <div class="row g-2 align-items-end mb-3">
            <div class="col-auto">
              <label class="form-label">From</label>
              <input type="date" class="form-control" v-model="periodFrom" />
            </div>
            <div class="col-auto">
              <label class="form-label">To</label>
              <input type="date" class="form-control" v-model="periodTo" />
            </div>
            <div class="col-auto">
              <button class="btn btn-primary" :disabled="reportLoading || !periodFrom || !periodTo" @click="loadReport">
                <span v-if="reportLoading" class="spinner-border spinner-border-sm me-2"></span>
                {{ reportLoading ? 'Loading...' : 'Run report' }}
              </button>
            </div>
          </div>

          <div v-if="report">
            <table class="table table-bordered mb-0">
              <tbody>
                <tr><th>Platform fee collected</th><td>{{ currency(report.platformFeeCollected) }}</td></tr>
                <tr><th>Account invoice collected</th><td>{{ currency(report.accountInvoiceCollected) }}</td></tr>
                <tr><th>Total collected (payable)</th><td>{{ currency(report.totalCollected) }}</td></tr>
                <tr><th>Driver fare memo (not payable, self-filed)</th><td>{{ currency(report.driverFareMemo) }}</td></tr>
                <tr><th>Remitted in period</th><td>{{ currency(report.remittedInPeriod) }}</td></tr>
                <tr><th>Entry count</th><td>{{ report.entryCount }}</td></tr>
                <tr><th>Current net payable (as of now)</th><td>{{ currency(report.netPayable) }}</td></tr>
                <tr>
                  <th>Reconciled</th>
                  <td>
                    <span v-if="report.reconciled" class="badge bg-success">Yes</span>
                    <span v-else class="badge bg-warning text-dark">
                      No &mdash; {{ report.missingAccrualCount }} settled ride(s) missing an accrual
                    </span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <div v-else class="text-muted">Choose a period and run the report.</div>
        </div>
      </div>
    </div>

    <div class="col-xl-6 col-12">
      <div class="card">
        <div class="card-header">
          <h5>Record a remittance</h5>
        </div>
        <div class="card-body">
          <div class="mb-2">
            <label class="form-label">Amount</label>
            <input type="number" step="0.01" min="0" class="form-control" v-model="remittanceAmount" />
          </div>
          <div class="mb-2">
            <label class="form-label">Period label</label>
            <input type="text" class="form-control" v-model="remittancePeriodLabel" placeholder="e.g. 2026-Q1" />
          </div>
          <div class="mb-2">
            <label class="form-label">Reference <span class="text-muted">(required — CRA's payment confirmation or a bank wire reference; this is what keeps a resubmission from double-counting)</span></label>
            <input type="text" class="form-control" v-model="remittanceReference" />
          </div>
          <button class="btn btn-primary" :disabled="remittanceSaving || !remittanceAmount || !remittanceReference" @click="submitRemittance">
            <span v-if="remittanceSaving" class="spinner-border spinner-border-sm me-2"></span>
            {{ remittanceSaving ? 'Saving...' : 'Record remittance' }}
          </button>
          <div v-if="remittanceMessage" class="mt-2" :class="remittanceMessageClass">{{ remittanceMessage }}</div>
        </div>
      </div>
    </div>

    <div class="col-xl-12">
      <div class="card">
        <div class="card-header">
          <h5>Ledger</h5>
        </div>
        <div class="card-body">
          <div v-if="ledgerLoaded">
            <DataTable :ajax="{ url: '/api/datatable/tax-reserve-entries' }"
                       ref="ledgerDataTable" class="table-responsive display" :columns="ledgerColumns" />
          </div>
          <div v-else class="text-center py-4">
            <span class="spinner-border spinner-border-sm me-2"></span> Loading...
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue';
import DataTable from 'datatables.net-vue3';
import DataTablesCore from 'datatables.net-bs5';
import { zonedDateTime, currency as currencyRenderer } from '../utility/DatatableRenderer';

DataTable.use(DataTablesCore);

const summaryLoaded = ref(false);
const summary = ref(null);
const ledgerLoaded = ref(false);
const reportLoading = ref(false);
const report = ref(null);
const remittanceSaving = ref(false);
const remittanceMessage = ref('');
const remittanceMessageClass = ref('');

const periodFrom = ref('');
const periodTo = ref('');
const remittanceAmount = ref('');
const remittancePeriodLabel = ref('');
const remittanceReference = ref('');

// Every reserve figure the API returns is a dollar BigDecimal already (Xxx, not XxxMinor) — see
// TaxReserveSummaryDto/TaxRemittancePeriodReportDto's dollar getters. Minor units only ever reach the
// browser on the ledger table (TaxReserveEntryDto has no dollar getter), which is why the DataTable
// column below goes through minorToCurrency instead.
function currency(value) {
  return currencyRenderer(value);
}

function minorToCurrency(value) {
  return currencyRenderer(value === null || value === undefined ? null : value / 100);
}

const summaryCards = computed(() => {
  if (!summary.value) {
    return [];
  }
  return [
    { label: 'Net payable', value: currency(summary.value.netPayable) },
    { label: 'Accrued (lifetime)', value: currency(summary.value.accrued) },
    { label: 'Swept (lifetime)', value: currency(summary.value.swept) },
    { label: 'Remitted (lifetime)', value: currency(summary.value.remitted) },
    { label: 'Not yet swept', value: currency(summary.value.unswept) },
    { label: 'Next filing date', value: summary.value.nextFilingDate || 'N/A' },
  ];
});

const ledgerColumns = [
  { data: 'effectiveAt', title: 'Date', render: zonedDateTime },
  { data: 'entryType', title: 'Type' },
  { data: 'remitter', title: 'Remitter' },
  { data: 'supplyCategory', title: 'Category' },
  { data: 'amountMinor', title: 'Amount', render: minorToCurrency },
  { data: 'entryStatus', title: 'Status' },
  { data: 'reviewReason', title: 'Review Reason' },
];

async function loadSummary() {
  try {
    const response = await fetch('/api/admin/tax-reserve/summary');
    if (response.ok) {
      summary.value = await response.json();
    }
  } catch (error) {
    console.error('Error loading tax reserve summary:', error);
  } finally {
    summaryLoaded.value = true;
  }
}

async function loadReport() {
  if (!periodFrom.value || !periodTo.value) {
    return;
  }
  reportLoading.value = true;
  try {
    // The backend period is a half-open [from, to) window. periodTo is a date-only picker value
    // ("2026-01-31"), which parses to UTC midnight at the *start* of that day -- so passed straight
    // through, an admin picking "To: Jan 31" intending to include the 31st would silently get a window
    // excluding it entirely. Advance by one day so the exclusive bound lands after the selected day.
    const from = new Date(periodFrom.value).toISOString();
    const toExclusive = new Date(periodTo.value);
    toExclusive.setUTCDate(toExclusive.getUTCDate() + 1);
    const to = toExclusive.toISOString();
    const response = await fetch(
        `/api/admin/tax-reserve/remittance-report?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`);
    if (response.ok) {
      report.value = await response.json();
    } else {
      report.value = null;
    }
  } catch (error) {
    console.error('Error loading the tax reserve remittance-period report:', error);
    report.value = null;
  } finally {
    reportLoading.value = false;
  }
}

async function submitRemittance() {
  remittanceSaving.value = true;
  remittanceMessage.value = '';
  try {
    const response = await fetch('/api/admin/tax-reserve/remittance', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        amount: remittanceAmount.value,
        periodLabel: remittancePeriodLabel.value,
        reference: remittanceReference.value,
      }),
    });
    if (response.ok) {
      remittanceMessage.value = 'Remittance recorded.';
      remittanceMessageClass.value = 'text-success';
      remittanceAmount.value = '';
      remittancePeriodLabel.value = '';
      remittanceReference.value = '';
      await loadSummary();
    } else {
      remittanceMessage.value = (await response.text()) || 'Failed to record remittance.';
      remittanceMessageClass.value = 'text-danger';
    }
  } catch (error) {
    remittanceMessage.value = 'Failed to record remittance.';
    remittanceMessageClass.value = 'text-danger';
    console.error('Error recording an HST remittance:', error);
  } finally {
    remittanceSaving.value = false;
  }
}

onMounted(async () => {
  await loadSummary();
  ledgerLoaded.value = true;
});
</script>
